'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { omit } from 'lodash';
import type { MultiValue } from 'react-select';

import { AppealStatus, GroupByOption, PaginatedResponse, WriteOffStatus } from '@repo/types';
import { SelectOption, showToast } from '@repo/ui';
import { QueryStringParams } from '@xtnd/web/shared/types';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';
import { arMatterApi } from '../api/matterDetailApi';

import { type DataTableColumn } from '@xtnd/web/shared/components/DataTable';

import {
  ArWriteOffFilter,
  WriteOff,
  ActionDialogMode,
  DialogState,
  ActionMenuItem,
  WriteOffTabType,
  WriteOffGroupByClient,
  WriteOffItem,
} from '../types/writeOffs';
import { INITIAL_FILTERS, APPEAL_STATUS_OPTIONS, DEFAULT_APPEAL_STATUSES, EXCLUDED_STATUS_FILTER_KEYS } from '../constants';
import { getGroupByColumns, getWriteOffColumns, createSelectColumn, getWriteOffRowKey } from '../components/write-offs/WriteOffColumns';
import { useInvoiceContext } from '../components/common/InvoiceContext';

// ============================================================================
// Filter Hooks (moved from FilterToolbars.tsx)
// ============================================================================

/**
 * Hook for getting theme-aware appeal status options.
 * Returns options formatted for the Select component based on dark/light mode.
 */
export function useAppealStatusOptions() {
  const { isDark } = useDarkMode();

  return useMemo<SelectOption[]>(() => {
    const mode = isDark ? 'dark' : 'light';
    const filteredOptions = omit(APPEAL_STATUS_OPTIONS[mode], EXCLUDED_STATUS_FILTER_KEYS);
    return Object.values(filteredOptions);
  }, [isDark]);
}

/**
 * Hook for getting selected status options for display in Select.
 * Returns empty array if using default selection (shows placeholder).
 */
export function useStatusSelected(appealStatus?: AppealStatus[]) {
  const selectStatusOptions = useAppealStatusOptions();

  return useMemo(() => {
    // Return empty if no explicit selection (uses default)
    if (!appealStatus || appealStatus.length === 0) return [];
    return selectStatusOptions.filter(option =>
      appealStatus.includes(option.value as AppealStatus)
    );
  }, [selectStatusOptions, appealStatus]);
}

/**
 * Hook for creating filter update handlers.
 * Returns update function and handleSelectedStatus for appeal status changes.
 */
export function useFilterUpdate(
  filters: ArWriteOffFilter,
  onChange: (next: ArWriteOffFilter) => void
) {
  const update = useCallback(
    (patch: Partial<ArWriteOffFilter>) => {
      onChange({ ...filters, ...patch });
    },
    [filters, onChange]
  );

  const handleSelectedStatus = useCallback(
    (selected: MultiValue<SelectOption> | null) => {
      const nextStatuses = (selected ?? []).map(option => option.value as AppealStatus);
      update({ appealStatus: nextStatuses.length ? nextStatuses : DEFAULT_APPEAL_STATUSES });
    },
    [update]
  );

  return { update, handleSelectedStatus };
}

// ============================================================================
// Sub-hooks and factories
// ============================================================================

/**
 * Hook for managing write-off tab state (invoices, clientName, display state)
 * Used to track which client's invoices are currently being displayed in detail view
 */
export function useWriteOffTabState() {
  const [invoices, setInvoices] = useState<WriteOff[] | null>(null);
  const [clientName, setClientName] = useState<string>('');

  const isDisplayInvoicesTable = useMemo(() => !!invoices, [invoices]);

  const resetInvoices = useCallback(() => {
    setInvoices(null);
    setClientName('');
  }, []);

  return {
    invoices,
    setInvoices,
    clientName,
    setClientName,
    isDisplayInvoicesTable,
    resetInvoices,
  };
}

/**
 * Hook for managing mutation state (tracking which rows are being mutated)
 * Used to track which invoice rows are currently undergoing async operations
 */
export function useWriteOffMutationState() {
  const [mutatingRows, setMutatingRows] = useState<Set<string>>(new Set());

  const handleMutationStateChange = useCallback((invoiceId: string, isPending: boolean) => {
    setMutatingRows(prev => {
      const next = new Set(prev);
      if (isPending) {
        next.add(invoiceId);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
  }, []);

  return {
    mutatingRows,
    handleMutationStateChange,
  };
}

/**
 * Factory function to create a local state update handler for grouped view collapse.
 * Used to remove an invoice from the local state when it's moved to another tab.
 */
export function createLocalStateUpdateHandler(
  setInvoices: React.Dispatch<React.SetStateAction<WriteOff[] | null>>,
  resetInvoices: () => void
) {
  return (invoiceId: string) => {
    setInvoices(prev => {
      if (!prev) return prev;
      const next = prev.filter(inv => inv.invoiceId !== invoiceId);
      if (next.length === 0) {
        resetInvoices();
        return null;
      }
      return next;
    });
  };
}

/**
 * Factory function to create a local state update handler for comment updates in grouped view.
 * Used to update an invoice's comment in the local state when a note-only update is performed.
 */
export function createLocalCommentUpdateHandler(
  setInvoices: React.Dispatch<React.SetStateAction<WriteOff[] | null>>
) {
  return (invoiceId: string, comment?: string) => {
    setInvoices(prev => {
      if (!prev) return prev;
      return prev.map(inv => (inv.invoiceId === invoiceId ? { ...inv, comment } : inv));
    });
  };
}

// ============================================================================
// Types for useWriteOffTab
// ============================================================================

/** Common props shared by all write-off tab components */
export interface WriteOffTabProps {
  isAdmin: boolean;
  tableParams: QueryStringParams;
  setTableParams: (params: Partial<QueryStringParams>) => void;
  data?: PaginatedResponse<WriteOff>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Configuration for useWriteOffTab hook */
export interface UseWriteOffTabConfig {
  tabType: WriteOffTabType;
  isAdmin: boolean;
  tableParams: QueryStringParams;
  setTableParams: (params: Partial<QueryStringParams>) => void;
  /** Dialog IDs to manage, e.g., ['approve', 'reject', 'note'] */
  dialogIds: string[];
  /** Action menu items - each should reference a dialogId to open */
  actions: (ActionMenuItem & { dialogId?: string })[];
  /** Whether this tab supports local state updates (removes invoice on action) */
  supportsLocalStateUpdate?: boolean;
  /** Callback when Activity icon is clicked */
  onOpenActivity?: (row: WriteOff) => void;
}

/**
 * Map dialog IDs to their corresponding ActionDialogMode.
 * 'approve' -> APPROVED, 'reject' -> REJECTED, 'archive' -> ARCHIVED, 'note' -> 'note'
 */
export const DIALOG_MODE_MAP: Record<string, ActionDialogMode> = {
  approve: WriteOffStatus.APPROVED,
  reject: WriteOffStatus.REJECTED,
  archive: WriteOffStatus.ARCHIVED,
  escalated: WriteOffStatus.ESCALATED,
  note: 'note',
};

// ============================================================================
// useWriteOffTab Hook
// ============================================================================

/**
 * Unified hook for write-off tab state management.
 * Consolidates dialog state, filter sync, mutation handlers, columns, and actions menu.
 */
export function useWriteOffTab(config: UseWriteOffTabConfig) {
  const {
    tabType,
    isAdmin,
    tableParams,
    setTableParams,
    dialogIds,
    actions,
    supportsLocalStateUpdate = false,
    onOpenActivity,
  } = config;

  // Tab state (invoices, clientName for grouped view)
  const {
    invoices,
    setInvoices,
    clientName,
    setClientName,
    isDisplayInvoicesTable,
    resetInvoices,
  } = useWriteOffTabState();

  // Selection state for bulk actions (e.g., Print PDF)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);

  const { openInvoiceDetailDrawer } = useInvoiceContext();

  // Mutation state
  const { mutatingRows, handleMutationStateChange } = useWriteOffMutationState();

  // Actions menu state
  const [actionsMenuOpen, setActionsMenuOpen] = useState<string | null>(null);
  const [menuTriggerElement, setMenuTriggerElement] = useState<HTMLElement | null>(null);

  // Filters
  const filters = (tableParams.filters as ArWriteOffFilter) ?? INITIAL_FILTERS;
  const [invoiceFilters, setInvoiceFilters] = useState<ArWriteOffFilter>(filters);

  // Reset selection when groupBy changes
  useEffect(() => {
    setSelectedKeys([]);
  }, [filters.groupBy]);

  // Sync invoice filters when main filters change (except when in invoice detail view)
  useEffect(() => {
    if (!isDisplayInvoicesTable) {
      setInvoiceFilters(filters);
    }
  }, [filters, isDisplayInvoicesTable]);

  // Dialog states - dynamically create based on dialogIds
  const [dialogStates, setDialogStates] = useState<
    Record<string, { isOpen: boolean; selectedWriteOff: WriteOffItem | null }>
  >(() => {
    const initial: Record<string, { isOpen: boolean; selectedWriteOff: WriteOffItem | null }> = {};
    dialogIds.forEach(id => {
      initial[id] = { isOpen: false, selectedWriteOff: null };
    });
    return initial;
  });

  // Dialog handlers
  const handleOpenDialog = useCallback((id: string, row: WriteOffItem) => {
    setDialogStates(prev => ({
      ...prev,
      [id]: { isOpen: true, selectedWriteOff: row },
    }));
    setActionsMenuOpen(null);
  }, []);

  const handleCloseDialog = useCallback((id: string) => {
    setDialogStates(prev => ({
      ...prev,
      [id]: { isOpen: false, selectedWriteOff: null },
    }));
  }, []);

  const dialogs = useMemo(() => {
    const result: Record<string, DialogState> = {};
    dialogIds.forEach(id => {
      result[id] = {
        isOpen: dialogStates[id]?.isOpen ?? false,
        selectedWriteOff: dialogStates[id]?.selectedWriteOff ?? null,
        open: (row: WriteOffItem) => handleOpenDialog(id, row),
        close: () => handleCloseDialog(id),
      };
    });
    return result;
  }, [dialogIds, dialogStates, handleOpenDialog, handleCloseDialog]);

  // Local state handlers for mutations
  const handleLocalStateUpdate = useCallback(
    createLocalStateUpdateHandler(setInvoices, resetInvoices),
    [setInvoices, resetInvoices]
  );

  const handleLocalCommentUpdate = useCallback(createLocalCommentUpdateHandler(setInvoices), [
    setInvoices,
  ]);

  // Filter and row handlers
  const handleChangeFilters = useMemo(
    () =>
      createHandleChangeFilters(filters, tableParams, setTableParams, setInvoices, setClientName),
    [filters, tableParams, setTableParams, setInvoices, setClientName]
  );

  const handleRowClicked = useMemo(
    () =>
      createHandleRowClicked(
        filters,
        setClientName,
        tableParams,
        setTableParams,
        openInvoiceDetailDrawer,
        tabType,
        dialogs
      ),
    [filters, setClientName, tableParams, setTableParams, openInvoiceDetailDrawer, tabType, dialogs]
  );

  // Pagination handler
  const handlePaginationChange = useCallback(
    (pagination: { page: number; limit: number }) => {
      setSelectedKeys([]);
      setTableParams({
        filters: { ...filters },
        page: pagination.page,
        limit: pagination.limit,
      });
    },
    [setTableParams, filters]
  );

  // Print PDF handler — resolves items from flattened row data and downloads collated PDF
  const handlePrintPdf = useCallback(async (flatData: any[]) => {
    if (selectedKeys.length === 0 || isPrinting) return;
    setIsPrinting(true);
    try {
      // Map selectedKeys (writeOffIds) back to { invoiceId, writeOffId } pairs
      const selectedRows = flatData.filter(
        row => selectedKeys.includes(getWriteOffRowKey(row, filters.groupBy)),
      );
      const items = selectedRows
        .filter(row => row.invoiceId && row.writeOffId)
        .map(row => ({ invoiceId: row.invoiceId, writeOffId: row.writeOffId }));
      if (items.length === 0) return;
      await arMatterApi.printInvoicesPdf(items);
    } catch (err) {
      if (err?.message === 'POPUP_BLOCKED') {
        showToast.warning('Pop-up blocked. Please allow pop-ups for this site and try again.');
      } else {
        showToast.error('Failed to generate PDF. Please try again.');
      }
    } finally {
      setIsPrinting(false);
    }
  }, [selectedKeys, isPrinting, filters.groupBy]);

  // Row class name getter
  const getRowClassName = useCallback(
    (row: WriteOff) => {
      const baseClass = 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150';
      if (mutatingRows.has(row.invoiceId)) {
        return `${baseClass} opacity-50 cursor-not-allowed pointer-events-none`;
      }
      return baseClass;
    },
    [mutatingRows]
  );

  // Build action items with dialog openers
  const actionItems: ActionMenuItem[] = useMemo(() => {
    return actions.map(action => ({
      ...action,
      onClick: (row: WriteOffItem) => {
        const dialog = action.dialogId ? dialogs[action.dialogId] : undefined;
        if (dialog) {
          dialog.open(row);
        } else {
          action.onClick?.(row);
        }
      },
    }));
  }, [actions, dialogs]);

  // Build action config for columns
  const actionConfig = useMemo(
    () => ({
      isAdmin,
      mutatingRows,
      actionsMenuOpen,
      setActionsMenuOpen,
      setMenuTriggerElement,
      menuTriggerElement,
      actions: actionItems,
    }),
    [isAdmin, mutatingRows, actionsMenuOpen, menuTriggerElement, actionItems]
  );

  // Columns
  const columns = useMemo(
    () => getGroupByColumns(tabType, onOpenActivity, actionConfig),
    [tabType, onOpenActivity, actionConfig]
  );

  const writeOffColumns = useMemo(
    () =>
      getWriteOffColumns({
        isAdmin,
        mutatingRows,
        actionsMenuOpen,
        setActionsMenuOpen,
        setMenuTriggerElement,
        menuTriggerElement,
        actions: actionItems,
      }),
    [isAdmin, mutatingRows, actionsMenuOpen, menuTriggerElement, actionItems]
  );

  return {
    // Tab state
    tabType,
    invoices,
    setInvoices,
    clientName,
    setClientName,
    isDisplayInvoicesTable,
    resetInvoices,

    // Selection state
    selectedKeys,
    setSelectedKeys,

    // Mutation state
    mutatingRows,
    handleMutationStateChange,

    // Filters
    filters,
    invoiceFilters,
    setInvoiceFilters,
    handleChangeFilters,

    // Dialogs
    dialogs,

    // Columns
    columns,
    writeOffColumns,

    // Handlers
    handleRowClicked,
    handlePaginationChange,
    handlePrintPdf,
    isPrinting,
    getRowClassName,
    handleLocalStateUpdate: supportsLocalStateUpdate ? handleLocalStateUpdate : undefined,
    handleLocalCommentUpdate,
  };
}

/**
 * Factory function to create handleChangeFilters handler
 * Handles filter changes and resets invoice detail view when groupBy changes
 *
 * @param filters - Current filter state
 * @param queryParams - Current query parameters
 * @param setQueryParams - Function to update query parameters
 * @param setInvoices - Function to set invoices array
 * @param setClientName - Function to set client name
 * @returns Handler function for filter changes
 */
const createHandleChangeFilters = (
  filters: ArWriteOffFilter,
  queryParams: QueryStringParams,
  setQueryParams: (params: Partial<QueryStringParams>) => void,
  setInvoices: (invoices: WriteOff[] | null) => void,
  setClientName: (name: string) => void
) => {
  return (nextFilters: ArWriteOffFilter) => {
    const groupBy = nextFilters.groupBy;
    if (groupBy !== filters.groupBy) {
      // Reset appealStatus to defaults when changing groupBy
      const resetFilters = {
        ...nextFilters,
        appealStatus: DEFAULT_APPEAL_STATUSES,
        statusExplicit: false,
      };
      setQueryParams({
        ...queryParams,
        page: 1,
        filters: resetFilters,
      });
      // Reset invoices when changing groupBy
      setInvoices(null);
      setClientName('');
      return;
    }

    setQueryParams({ ...queryParams, page: 1, filters: nextFilters });
  };
};

/**
 * Factory function to create handleRowClicked handler
 * Handles row click events, opening invoice detail view when grouped by client
 *
 * @param filters - Current filter state
 * @param setClientName - Function to set client name
 * @param queryParams - Current query parameters
 * @param setQueryParams - Function to update query parameters
 * @returns Handler function for row clicks
 */
const createHandleRowClicked = (
  filters: ArWriteOffFilter,
  setClientName: (name: string) => void,
  queryParams: QueryStringParams,
  setQueryParams: (params: Partial<QueryStringParams>) => void,
  openInvoiceDetailDrawer: (invoice: string, options?: any) => void,
  tabType: WriteOffTabType,
  dialogs: Record<string, DialogState>
) => {
  return (row: WriteOff | WriteOffGroupByClient) => {
    // When grouped by client, check if row has clientId (WriteOffGroupByClient type)
    if (filters && filters.groupBy === GroupByOption.CLIENT && 'clientId' in row && row.clientId) {
      // Set entityId in filters and call API again (like collections-queue)
      const newFilters = { ...filters, entityId: row.clientId };
      setQueryParams({ ...queryParams, filters: newFilters, page: 1 });
      setClientName(row.clientName || '');
      return;
    }

    const writeOffRow = row as WriteOff;
    // Get writeOffId - check if it's directly on the row (flattened data in "All" tab)
    // or inside the writeOffs array (regular tabs)
    const writeOffId = (writeOffRow as any).writeOffId || writeOffRow.writeOffs?.[0]?.writeOffId;
    openInvoiceDetailDrawer(writeOffRow.invoiceId, {
      writeOffId,
      writeOffStatus: writeOffRow.writeOffStatus,
      tabType,
      actions: {
        onApprove: () => dialogs['approve']?.open(writeOffRow as any),
        onReject: () => dialogs['reject']?.open(writeOffRow as any),
        onProcessed: () => dialogs['archive']?.open(writeOffRow as any),
        onComment: () => dialogs['note']?.open(writeOffRow as any),
      },
    });

    // For NONE groupBy, handle individual invoice clicks if needed
    // Currently no action needed for individual invoices
  };
};

// ============================================================================
// Selection Hook (shared across tabs and ClientInvoicesTable)
// ============================================================================

/**
 * Hook that encapsulates row selection logic for Write Off tables.
 * Returns columns with a prepended checkbox column (when groupBy !== CLIENT).
 *
 * Each call creates its own handlers — state is NOT shared between callers.
 */
export function useWriteOffSelection(
  selectedKeys: string[],
  setSelectedKeys: (keys: string[]) => void,
  groupBy: GroupByOption,
  baseColumns: DataTableColumn[],
  data: any[],
) {
  const handleSelectRow = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, row: any) => {
      e.stopPropagation();
      const key = getWriteOffRowKey(row, groupBy);
      const next = e.target.checked
        ? [...selectedKeys, key]
        : selectedKeys.filter(k => k !== key);
      setSelectedKeys(next);
    },
    [selectedKeys, setSelectedKeys, groupBy]
  );

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        const keys = data.map(row => getWriteOffRowKey(row, groupBy));
        setSelectedKeys(keys);
      } else {
        setSelectedKeys([]);
      }
    },
    [data, setSelectedKeys, groupBy]
  );

  const columns = useMemo(() => {
    if (groupBy === GroupByOption.CLIENT) return baseColumns;
    const selectCol = createSelectColumn(
      selectedKeys,
      handleSelectRow,
      handleSelectAll,
      data.length,
    );
    return [selectCol, ...baseColumns];
  }, [baseColumns, groupBy, selectedKeys, handleSelectRow, handleSelectAll, data]);

  return { columns };
}

/** Return type of useWriteOffTab hook */
export type UseWriteOffTabReturn = ReturnType<typeof useWriteOffTab>;
